import { useEffect, useState } from 'react';
import { generatePath, useLocation, useNavigate } from 'react-router-dom';
import { Tab, Tabs } from '@paysera/ui';
import { useTranslation } from 'react-i18next';
import { useGetFunctionalitiesQuery } from '@app/api/functionalitiesApi';
import { useGetInvestmentOpportunitiesQuery, useGetMyInvestmentsQuery } from '@app/api/investmentsApi';
import { useGetTermsAndConditionsQuery } from '@app/api/termsAndConditionsApi';
import { TermsAndConditionsType } from '@app/api/types';
import { PageLoader } from '@app/components/PageLoader';
import { ROUTES } from '@app/configs/routes';
import { getInvestorLocale } from '@app/utils/getInvestorLocale';
import InvestorPortfolio from '@modules/investor/components/InvestorPortfolio';
import TermsAndConditions from '@modules/investor/components/TermsAndConditions';
import { createInvestmentsTableConfig } from '../config/investmentsTableConfig';
import { createOpportunitiesTableConfig } from '../config/opportunitiesTableConfig';
import { PER_PAGE } from '../config/table';
import { TAB_KEYS } from '../config/tabs';
import InvestorTable from './common/InvestorTable';
import NoOpportunities from './NoOpportunities';
const Investor = () => {
    var _a;
    const { t } = useTranslation();
    const { data: functionalities } = useGetFunctionalitiesQuery();
    const navigate = useNavigate();
    const { search } = useLocation();
    const [opportunitiesPage, setOpportunitiesPage] = useState(1);
    const [myInvestmentsPage, setMyInvestmentsPage] = useState(1);
    const [opportunitiesPerPage, setOpportunitiesPerPage] = useState(PER_PAGE);
    const [myInvestmentsPerPage, setMyInvestmentsPerPage] = useState(PER_PAGE);
    const [activeTab, setActiveTab] = useState(TAB_KEYS.OPPORTUNITIES);
    const locale = getInvestorLocale();
    const [opportunitiesFilters, setOpportunitiesFilters] = useState({});
    const [myInvestmentsFilters, setMyInvestmentsFilters] = useState({});
    const [currentOpportunitiesSorting, setCurrentOpportunitiesSorting] = useState({});
    const [currentMyInvestmentsSorting, setCurrentMyInvestmentsSorting] = useState({});
    const { data: termsAndConditions, isLoading: loadingTermsAndConditions } = useGetTermsAndConditionsQuery(TermsAndConditionsType.INVESTOR);
    const { data: opportunitiesData, isLoading: loadingOpportunities, isFetching: fetchingOpportunities, } = useGetInvestmentOpportunitiesQuery({
        locale,
        page: opportunitiesPage,
        perPage: opportunitiesPerPage,
        orderBy: currentOpportunitiesSorting.orderBy,
        order: currentOpportunitiesSorting.order,
        amountFrom: opportunitiesFilters.amountFrom,
        amountTo: opportunitiesFilters.amountTo,
        periodFrom: opportunitiesFilters.periodFrom,
        periodTo: opportunitiesFilters.periodTo,
        purposeIds: opportunitiesFilters.purposeIds,
        riskScores: opportunitiesFilters.riskScores,
    }, { skip: !(termsAndConditions === null || termsAndConditions === void 0 ? void 0 : termsAndConditions.accepted) });
    const opportunities = opportunitiesData || {
        items: [],
        pagination: { totalPages: 0, page: 0, perPage: 0, totalItems: 0 },
    };
    const { data: myInvestments, isLoading: loadingInvestments, isFetching: fetchingInvestments, } = useGetMyInvestmentsQuery({
        locale,
        page: myInvestmentsPage,
        perPage: myInvestmentsPerPage,
        orderBy: currentMyInvestmentsSorting.orderBy,
        order: currentMyInvestmentsSorting.order,
        loanAmountFrom: myInvestmentsFilters.amountFrom,
        loanAmountTo: myInvestmentsFilters.amountTo,
        loanPeriodFrom: myInvestmentsFilters.periodFrom,
        loanPeriodTo: myInvestmentsFilters.periodTo,
        loanPurposeIds: myInvestmentsFilters.purposeIds,
        investmentStatuses: myInvestmentsFilters.investmentStatuses,
    }, { skip: !(termsAndConditions === null || termsAndConditions === void 0 ? void 0 : termsAndConditions.accepted) });
    const termsAndConditionsAgreements = termsAndConditions === null || termsAndConditions === void 0 ? void 0 : termsAndConditions.agreements;
    const showTermsAndConditions = !loadingTermsAndConditions && !(termsAndConditions === null || termsAndConditions === void 0 ? void 0 : termsAndConditions.accepted);
    const handleTabChange = (tabKey) => {
        setActiveTab(tabKey);
        const currentParams = new URLSearchParams(search);
        currentParams.set('tab', tabKey);
        const newSearch = currentParams.toString();
        navigate(`?${newSearch}`, { replace: true });
    };
    useEffect(() => {
        const query = new URLSearchParams(search);
        const tabSelected = query.get('tab');
        if (tabSelected) {
            setActiveTab(tabSelected);
        }
    }, [search]);
    const handleOpportunitiesSortingChange = (orderBy, order) => {
        setCurrentOpportunitiesSorting({ orderBy, order });
        setOpportunitiesPage(1);
    };
    const handleMyInvestmentsSortingChange = (orderBy, order) => {
        setCurrentMyInvestmentsSorting({ orderBy, order });
        setMyInvestmentsPage(1);
    };
    const handleOpportunitiesFilterChange = (filters) => {
        setOpportunitiesFilters(filters);
        setOpportunitiesPage(1);
    };
    const handleMyInvestmentsFilterChange = (filters) => {
        setMyInvestmentsFilters(filters);
        setMyInvestmentsPage(1);
    };
    const handleOpportunitiesPerPageChange = (perPage) => {
        setOpportunitiesPerPage(perPage);
        setOpportunitiesPage(1);
    };
    const handleMyInvestmentsPerPageChange = (perPage) => {
        setMyInvestmentsPerPage(perPage);
        setMyInvestmentsPage(1);
    };
    if (loadingTermsAndConditions) {
        return <PageLoader />;
    }
    const handleGoToDetails = (id) => {
        navigate(generatePath(ROUTES.investmentDetails, { id }));
    };
    const handleGoToInvest = (id) => {
        navigate(generatePath(ROUTES.invest, { id }));
    };
    const showInvestingFunctionality = (_a = functionalities === null || functionalities === void 0 ? void 0 : functionalities.investing) === null || _a === void 0 ? void 0 : _a.enabled;
    return (<>
            <h1 className="mb-4 md:mb-6 headline-m">{t('financing.investing_page.investing')}</h1>
            {showTermsAndConditions && <TermsAndConditions agreements={termsAndConditionsAgreements}/>}

            {!showTermsAndConditions && (<>
                    <InvestorPortfolio />
                    <Tabs selectedKey={activeTab} onSelectionChange={(key) => handleTabChange(key)}>
                        {[
                showInvestingFunctionality ? (<Tab key={TAB_KEYS.OPPORTUNITIES} label={t('financing.investing_page.opportunities')} tabKey={TAB_KEYS.OPPORTUNITIES}>
                                        {!opportunities.items.length ? (<div className="min-h-[750px]">
                                                {(loadingOpportunities || fetchingOpportunities) && <PageLoader />}
                                                {!loadingOpportunities && !fetchingOpportunities && (<NoOpportunities filters={opportunitiesFilters} onFilterChange={setOpportunitiesFilters}/>)}
                                            </div>) : (<div className="min-h-[750px]">
                                                <InvestorTable data={opportunities.items} config={createOpportunitiesTableConfig(t)} pagination={opportunities.pagination} onPageChange={setOpportunitiesPage} onPerPageChange={handleOpportunitiesPerPageChange} onDetailsClick={handleGoToInvest} initialLoading={loadingOpportunities} isFetching={fetchingOpportunities} onSortChange={handleOpportunitiesSortingChange} onFilterChange={handleOpportunitiesFilterChange} filters={opportunitiesFilters}/>
                                            </div>)}
                                    </Tab>) : null,
                <Tab key={TAB_KEYS.MY_INVESTMENTS} label={t('financing.investing_page.my_investments')} tabKey={TAB_KEYS.MY_INVESTMENTS}>
                                    {!(myInvestments === null || myInvestments === void 0 ? void 0 : myInvestments.items.length) ? (<div className="min-h-[750px]">
                                            {(loadingInvestments || fetchingInvestments) && <PageLoader />}
                                            {!loadingInvestments && !fetchingInvestments && (<NoOpportunities isInvestment filters={myInvestmentsFilters} onFilterChange={setMyInvestmentsFilters}/>)}
                                        </div>) : (<div className="min-h-[750px]">
                                            <InvestorTable data={myInvestments.items} config={createInvestmentsTableConfig(t)} pagination={myInvestments.pagination} onPageChange={setMyInvestmentsPage} onPerPageChange={handleMyInvestmentsPerPageChange} onDetailsClick={handleGoToDetails} initialLoading={loadingInvestments} isFetching={fetchingInvestments} onSortChange={handleMyInvestmentsSortingChange} onFilterChange={handleMyInvestmentsFilterChange} filters={myInvestmentsFilters}/>
                                        </div>)}
                                </Tab>,
            ].filter(Boolean)}
                    </Tabs>
                </>)}
        </>);
};
export default Investor;
